import { useCallback } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import illustration from '@app/illu/Image/ShowPhone.svg';
import { CheckboxBlockList, CheckboxBlockField } from '@app/form';
import { SubmitButton, TANDEMZ_PANEL } from '@app/core';
import { Formik, Form, FormikHelpers } from 'formik';
import { useAddToPanel } from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
  },
  leftBlock: {
    flex: 1,
    maxWidth: theme.maxContentWidth.mediumBlock,
    [theme.media.mobile]: {
      maxWidth: '100%',
    },
  },
  rightBlock: {
    flex: 1,
    marginLeft: theme.marginBase * 7,
    [theme.media.mobile]: {
      display: 'none',
    },
  },
  title: {
    ...theme.fonts.h5,
    marginBottom: theme.marginBase * 3,
  },
  description: {
    ...theme.fonts.caption,
    marginBottom: theme.marginBase * 3,
    color: theme.colors.inkSecondary,
  },
  illustration: {
    margin: 'auto',
    maxWidth: '100%',
  },
  input: {
    marginBottom: theme.marginBase * 2,
  },
}));

interface Props {
  participantId: string;
  onSubmit: (joinedPanel: boolean) => any;
  countryKey?: string;
}

const initialValues = {
  addToPanel: undefined,
};

const validationSchema = Yup.object().shape({
  addToPanel: Yup.boolean().required(),
});

export const AddToPanel = ({ onSubmit, countryKey, participantId }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const { mutateAsync: addToPanel } = useAddToPanel();
  const _onSubmit = useCallback(
    async (
      values: typeof initialValues,
      helpers: FormikHelpers<typeof initialValues>,
    ) => {
      try {
        if (!values.addToPanel) {
          onSubmit(false);
          return;
        }
        await addToPanel({
          participantId,
          panelId: TANDEMZ_PANEL,
          countryKey,
        });

        onSubmit(true);
      } catch (e) {
        helpers.setStatus(e);
      } finally {
        helpers.setSubmitting(false);
      }
    },
    [addToPanel, onSubmit, participantId, countryKey],
  );

  return (
    <div className={classes.container}>
      <Formik
        initialValues={initialValues}
        onSubmit={_onSubmit}
        validationSchema={validationSchema}
      >
        {({ values }) => (
          <Form>
            <div className={classes.leftBlock}>
              <h1 className={classes.title}>
                <Trans i18nKey="application.addToPanel.title" />
              </h1>
              <p className={classes.description}>
                <Trans i18nKey="application.addToPanel.description" />
              </p>

              <CheckboxBlockList
                label={t('application.addToPanel.choices.label')}
                vertical
              >
                <CheckboxBlockField
                  type="radio"
                  name="addToPanel"
                  value={true}
                  label={t('application.addToPanel.choices.yes')}
                  trackingName="Add to Panel"
                />
                <CheckboxBlockField
                  type="radio"
                  name="addToPanel"
                  value={false}
                  label={t('application.addToPanel.choices.no')}
                  trackingName="Decline Panel"
                />
              </CheckboxBlockList>
              <SubmitButton
                color="green"
                disabled={values.addToPanel === undefined}
                trackingName="Submit add to Panel"
                fullMobileWidth
              >
                <Trans i18nKey="application.addToPanel.send" />
              </SubmitButton>
            </div>
          </Form>
        )}
      </Formik>
      <div className={classes.rightBlock}>
        <img
          className={classes.illustration}
          src={illustration}
          alt="User test illustration"
        />
      </div>
    </div>
  );
};
